/**
 * Parsers for the backend's JSON-string payloads used by the Medium-Term
 * Forecaster. Pure functions: no Vue, no store, no DOM. Unit-tested with
 * `npm run test:unit`.
 *
 * Backend conventions (see docs/forecast-upgrade-plan.md §1):
 *  - every operation returns `data` as a JSON string; the stores strip
 *    backslashes before handing it to the page;
 *  - `forecastTool` is double-encoded (`json.dumps(df.to_json())`), so after
 *    the backslash strip it is a JSON string wrapped in quotes;
 *  - `[]` is the stores' "nothing loaded yet" placeholder.
 */

/**
 * Parse a backend JSON string tolerant of the placeholder `[]`, of the
 * double-encoded `forecastTool` payload, and of already-parsed objects.
 * Returns `null` when there is nothing usable.
 */
export function parseBackendJson(value) {
  if (value == null) return null;
  if (typeof value !== "string") return value;
  let s = value.trim();
  if (s === "" || s === "[]") return null;
  if (s.startsWith('"') && s.endsWith('"') && s.length >= 2) {
    // double-encoded document: the inner text is itself JSON
    s = s.slice(1, -1);
  }
  try {
    const parsed = JSON.parse(s);
    if (parsed && typeof parsed === "object" && Object.keys(parsed).length === 0) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

/**
 * Consensus input table: `{quarter: {indicator: value|null}}`.
 * A row holding a `null` in any quarter is a group header (LOCAL / INTERNATIONAL).
 * Values are kept as the backend's 2-decimal strings so the Apply payload stays
 * byte-identical with the pre-refactor page.
 */
export function parseConsensusTable(value) {
  const data = parseBackendJson(value);
  if (!data) return { quarters: [], rows: [] };
  const quarters = Object.keys(data);
  const names = Object.keys(data[quarters[0]] || {});
  const rows = names.map((name) => {
    let header = false;
    const cells = quarters.map((q) => {
      const v = data[q] ? data[q][name] : null;
      if (v == null) {
        header = true;
        return null;
      }
      return Number(v).toFixed(2);
    });
    return { name, data: cells, header };
  });
  return { quarters, rows };
}

/**
 * Result of `forecastTool`: `{outputName: {date: value}}` with the first date
 * being the last observation. Returns dates (sorted as delivered), rows of
 * numbers and the list of variable names in delivery order.
 */
export function parseForecastResult(value) {
  const data = parseBackendJson(value);
  if (!data) return { dates: [], rows: [], variables: [] };
  const variables = Object.keys(data);
  const dates = Object.keys(data[variables[0]] || {});
  const rows = variables.map((name) => ({
    name,
    data: dates.map((d) => toNumberOrNull(data[name][d])),
  }));
  return { dates, rows, variables };
}

/**
 * Series maps (`teForecast`, `lastSemester`, `consensusResults`):
 * `{variable: {date: value}}`. Returns `{}` for the placeholder.
 */
export function parseSeriesMap(value) {
  const data = parseBackendJson(value);
  return data && typeof data === "object" ? data : {};
}

/** `{date: value}` → `[[date, value], …]` with numbers, skipping nulls. */
export function seriesToPairs(series) {
  if (!series) return [];
  return Object.keys(series)
    .map((d) => [d, toNumberOrNull(series[d])])
    .filter((p) => p[1] != null);
}

/**
 * Build the `forecastTool` payload exactly as the original page did:
 * `JSON.stringify({country, data:[{key, values:[{header, value}]}], endogenous})`
 * with backslashes removed. Header rows are skipped.
 */
export function buildAppJson({ country, quarters, rows, endogenous }) {
  const data = [];
  for (const row of rows) {
    if (row.header) continue;
    const values = row.data.map((v, j) => ({ header: quarters[j], value: v }));
    data.push({ key: row.name, values });
  }
  return JSON.stringify({ country, data, endogenous: !!endogenous }).replaceAll("\\", "");
}

export function toNumberOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** `2026-09-25` → `2026/09/25` (the page's display convention for tags). */
export function slashDate(d) {
  return d ? String(d).replaceAll("-", "/") : "";
}
